import { useEffect, useState } from "react";

import clsx from "clsx";
import { saveAs } from "file-saver";
import html2pdf from "html2pdf.js";
import Papa from "papaparse";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useCustomMetricsContext } from "@features/custom-metrics/hooks/custom-metrics-provider";
import {
  useMetricsExplorerContext,
  useMetricsExplorerStateContext,
} from "@features/metrics-explorer/hooks";
import { formatDateRange } from "@features/sales-v3/utils/format-date-range";
import { EllipsisMenu } from "@features/shared";
import { type ITimeGranularityV2 } from "@north-beam/nb-common";
import { AccountingModeSwitch } from "@shared/accounting-mode-switch";
import { AttributionModelAndWindowSelect } from "@shared/attribution-model-and-window-select";
import { TertiaryButton } from "@shared/buttons";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { DateFilters } from "../date-filters";
import { FilterQueryInput } from "../filter-query-input";
import { SaveSavedViewsDropdown } from "../saved-views/save-saved-views-dropdown";
import { SavedViewsDropdown } from "../saved-views/saved-views-dropdown";

import { AdvancedSearchBox } from "./advanced-search-box";
import { tileToExportData } from "./utils";

export const TopBar = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { isDemoMode } = usePrefs();
  const { user } = useUser();
  const { addToast } = useToasts();

  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    selectedSavedView,
    timeGranularity,
    filterQuery,
    advancedSearch,
    dateRange,
    tiles,
    setAttributionModel,
    setAttributionWindow,
    setAccountingMode,
    setTimeGranularity,
    setDateRange,
    setFilterQuery,
    setAdvancedSearch,
  } = useMetricsExplorerStateContext();
  const {
    filters: { dateRangeInterval },
    metricsData,
    pdfExportAreaRef,
    buildMetrics,
  } = useMetricsExplorerContext();
  const { openModal: openCustomMetricsModal } = useCustomMetricsContext();

  const [isAdvancedSearch, setIsAdvancedSearch] = useState(
    advancedSearch ? true : false,
  );

  useEffect(() => {
    if (advancedSearch) {
      setIsAdvancedSearch(false);
      setTimeout(() => setIsAdvancedSearch(true), 0);
    } else {
      setIsAdvancedSearch(false);
    }
  }, [advancedSearch, selectedSavedView]);

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    filterQuery,
    dateRange,
  };

  const isAttributionWindowVisible = accountingMode === "accrual";
  const isExportDisabled = metricsData.filter((e) => e.data).length === 0;

  return (
    <div className="px-6">
      <div className="border-b border-v2-secondary py-4">
        <div className="flex items-center mb-4">
          <div className="flex-1 flex items-center gap-2">
            <div className="text-2xl font-bold max-w-sm truncate">
              {generateAnonBase64Hash({
                value: selectedSavedView?.name,
                prefix: "quick-filter",
                doAnon: isDemoMode && !selectedSavedView?.is_template,
              })}
            </div>
            <SavedViewsDropdown />
          </div>
          <div className="flex items-end justify-end gap-4 flex-1">
            <div className="flex-1">
              {!isAdvancedSearch && (
                <FilterQueryInput
                  label={
                    <div className="flex items-center justify-between mt-0.5 mb-1">
                      <label className="label mb-0 text-v2-primary">
                        SEARCH
                      </label>
                      <TertiaryButton
                        className={clsx(
                          "m-0 p-0 leading-3 bg-v2-primary flex items-center gap-1",
                          "group hover:text-cosmic+2",
                        )}
                        onClick={() => {
                          prettyLogEventPage({
                            domain: "Metrics Explorer",
                            action: "onChange",
                            additionalActionInfo: "Switch to advanced search",
                            data: {
                              pageContext,
                            },
                          });

                          setIsAdvancedSearch(true);
                          setFilterQuery(null);
                        }}
                      >
                        <i className="fa-regular fa-gears text-xxs group-hover:text-v2-action" />
                        Advanced search
                      </TertiaryButton>
                    </div>
                  }
                  value={filterQuery}
                  onChange={(value) => {
                    prettyLogEventPage({
                      domain: "Metrics Explorer",
                      action: "onChange",
                      additionalActionInfo: "Set Filter Query",
                      data: {
                        newFilterQuery: value,
                        pageContext,
                      },
                    });

                    setFilterQuery(value);
                  }}
                />
              )}
            </div>
            <div className="flex items-center gap-3 pt-5">
              <SaveSavedViewsDropdown />
              <EllipsisMenu>
                <EllipsisMenu.Item
                  as="button"
                  disabled={isExportDisabled}
                  className={clsx({ "cursor-default": isExportDisabled })}
                  onClick={async () => {
                    if (!pdfExportAreaRef) {
                      return;
                    }

                    prettyLogEventPage({
                      domain: "Metrics Explorer",
                      action: "onClick",
                      additionalActionInfo: "Export image",
                      data: {
                        pageContext,
                      },
                    });

                    const elementsToHide = document.querySelectorAll(
                      ".__hide-on-pdf-export",
                    );
                    document.body.classList.add("__pdf-export-fix");

                    for (const el of elementsToHide) {
                      el.classList.add("hidden");
                    }

                    const rect = pdfExportAreaRef.getBoundingClientRect();

                    await html2pdf()
                      .set({
                        margin: [20, 40],
                        filename: "metrics-explorer.pdf",
                        image: { type: "jpeg", quality: 0.98 },
                        html2canvas: {
                          scale: 1,
                          dpi: 600,
                          useCORS: true,
                        },
                        jsPDF: {
                          unit: "px",
                          format: [rect.width + 80, rect.height + 40],
                          hotfixes: ["px_scaling"],
                          orientation: "landscape",
                        },
                      })
                      .from(pdfExportAreaRef)
                      .save();

                    for (const el of elementsToHide) {
                      el.classList.remove("hidden");
                    }
                    document.body.classList.remove("__pdf-export-fix");
                  }}
                >
                  <span
                    className={clsx("flex items-center gap-2", {
                      "text-gray": isExportDisabled,
                    })}
                  >
                    <span className="w-4 flex items-center justify-center">
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-chart-line text-xxs",
                          {
                            "text-cosmic": !isExportDisabled,
                            "text-gray": isExportDisabled,
                          },
                        )}
                      />
                    </span>
                    Export chart
                  </span>
                </EllipsisMenu.Item>
                <EllipsisMenu.Item
                  as="button"
                  disabled={isExportDisabled}
                  className={clsx({ "cursor-default": isExportDisabled })}
                  onClick={async () => {
                    prettyLogEventPage({
                      domain: "Metrics Explorer",
                      action: "onClick",
                      additionalActionInfo: "Export data",
                      data: {
                        pageContext,
                      },
                    });

                    const header: string[] = [];
                    const entries: string[][] = [];

                    metricsData.forEach((metricData, metricIndex) => {
                      const tile = tiles.find(
                        (e) => e.id === metricData.tileId,
                      );
                      const data = metricData.data;

                      if (!tile || !data) {
                        return;
                      }

                      const metrics = buildMetrics({
                        attributionModel:
                          tile.attributionModel ?? attributionModel,
                        attributionWindow:
                          tile.attributionWindow ?? attributionWindow,
                        accountingMode: tile.accountingMode ?? accountingMode,
                      });

                      const metric = metrics.find(
                        (e) => e.id === tile?.metricId,
                      );

                      if (!metric) {
                        return;
                      }

                      const result = tileToExportData({
                        tile,
                        metric,
                        metricData: data,
                        timezone: user.timezone,
                      });

                      const name = tile.name || metric.name;

                      header.push("date", name);

                      result.forEach((row, rowIndex) => {
                        if (!entries[rowIndex]) {
                          entries.push([]);
                        }

                        Object.values(row).forEach((value, valueIndex) => {
                          entries[rowIndex][metricIndex * 2 + valueIndex] =
                            value;
                        });
                      });
                    });

                    if (entries.length > 0) {
                      const csvString = Papa.unparse([header, ...entries]);

                      const blob = new Blob([csvString], {
                        type: "text/plain;charset=utf-8",
                      });

                      saveAs(blob, "metrics-explorer.csv");
                    }
                  }}
                >
                  <span
                    className={clsx("flex items-center gap-2", {
                      "text-gray": isExportDisabled,
                    })}
                  >
                    <span className="w-4 flex items-center justify-center">
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-table text-xxs",
                          {
                            "text-cosmic": !isExportDisabled,
                            "text-gray": isExportDisabled,
                          },
                        )}
                      />
                    </span>
                    Export CSV
                  </span>
                </EllipsisMenu.Item>
                <EllipsisMenu.Item
                  as="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(window.location.href);
                    addToast("Copied to clipboard!", { appearance: "success" });
                  }}
                >
                  <span className="flex items-center gap-2">
                    <span className="w-4 flex items-center justify-center">
                      <i className="fa-regular fa-sharp fa-link text-xxs text-cosmic" />
                    </span>
                    Share
                  </span>
                </EllipsisMenu.Item>
                <EllipsisMenu.Item as="button" onClick={openCustomMetricsModal}>
                  <span className="flex items-center gap-2">
                    <span className="w-4 flex items-center justify-center">
                      <i className="fa-regular fa-sharp fa-function text-xxs text-cosmic" />
                    </span>
                    Custom metrics
                  </span>
                </EllipsisMenu.Item>
              </EllipsisMenu>
            </div>
          </div>
        </div>
        {isAdvancedSearch && (
          <div className="mb-4">
            <AdvancedSearchBox
              value={advancedSearch}
              onChange={(value) => {
                prettyLogEventPage({
                  domain: "Metrics Explorer",
                  action: "onChange",
                  additionalActionInfo: "Set Advanced search",
                  data: {
                    pageContext,
                  },
                });

                setAdvancedSearch(value);
              }}
              onSwitchToSimpleSearch={() => {
                prettyLogEventPage({
                  domain: "Metrics Explorer",
                  action: "onChange",
                  additionalActionInfo: "Switch to simple search",
                  data: {
                    pageContext,
                  },
                });

                setIsAdvancedSearch(false);
                setAdvancedSearch(null);
              }}
            />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <AccountingModeSwitch
            value={accountingMode}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Set Accounting Mode",
                data: {
                  newAccountingMode: value,
                  pageContext,
                },
              });

              setAccountingMode(value);
            }}
          />
          <AttributionModelAndWindowSelect
            timeGranularity="daily"
            isAttributionWindowVisible={isAttributionWindowVisible}
            attributionModel={attributionModel}
            attributionWindow={attributionWindow}
            onAttributionModelChange={(value) => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Set Attribution Model",
                data: {
                  newAttributionModel: value,
                  pageContext,
                },
              });

              setAttributionModel(value);
            }}
            onAttributionWindowChange={(value) => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Set Attribution Window",
                data: {
                  newAttributionWindow: value,
                  pageContext,
                },
              });

              setAttributionWindow(value);
            }}
          />
          <TimeGranularityV2Select
            label="GRANULARITY"
            timeGranularity={timeGranularity}
            hideHourlyOption
            onChange={(value: ITimeGranularityV2) => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Set Time Granularity",
                data: {
                  newTimeGranularity: value,
                  pageContext,
                },
              });

              setDateRange({
                startDate: dateRangeInterval.startDate,
                endDate: dateRangeInterval.endDate,
                type: "fixed",
              });
              setTimeGranularity(value);
            }}
            width="92px"
            icon={null}
          />
          <div className="text-xs">
            <div
              className={clsx(
                "text-xs text-v2-primary mb-1",
                "leading-4 tracking-[0.025em] mt-[3px] cursor-default",
              )}
            >
              TIME PERIOD
              <span className="ml-2 text-gray+1">
                {formatDateRange(dateRangeInterval)}
              </span>
            </div>
            <DateFilters />
          </div>
        </div>
      </div>
    </div>
  );
};
